import api from './api';
import type { OutputChannelConfig } from './output-channels-api';
export type { OutputChannelConfig } from './output-channels-api';

// ============================================================================
// DYNAMIC ANALYSIS API - PHASE 6 IMPLEMENTATION
// ============================================================================

// ============================================================================
// TYPES
// ============================================================================

// Phase 1: Definition job config (creates entries in analysis_jobs_dynamic)
export interface DynamicJobsDefinitionConfig {
  timezone: string;
  analysisDescription?: string;
  outputChannel?: OutputChannelConfig | null;
  dynamicAnalysisSettings: {
    enabled: boolean;
    refreshOnSchedule: boolean;
  };
}

// Phase 2: Execution job config (runs analysis on entries)
export interface DynamicJobsExecutionConfig {
  timezone: string;
  parentJobId: string;
  analysisDescription?: string;
  executionSettings: {
    enabled: boolean;
  };
}

export interface DynamicAnalysisJob {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  type: 'dynamic_jobs_definition';
  status: 'active' | 'inactive' | 'error';
  enabled: boolean;
  config: DynamicJobsDefinitionConfig;
  schedule: string;
  lastRunAt?: string;
  nextRunAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

export interface ExecutionJob {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  type: 'dynamic_jobs_execution';
  status: 'active' | 'inactive' | 'error';
  enabled: boolean;
  config: DynamicJobsExecutionConfig;
  schedule: string;
  lastRunAt?: string;
  nextRunAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

export interface DynamicJobEntry {
  id: string;
  analysis_job_id: string;  // Parent job ID from analysis_jobs table
  data_source_account_id: string;
  dynamic_config: {
    dynamicAnalysisConfig: {
      analysisLevel: 'transactions' | 'transaction_details';
      analysisFilter: {
        logic: 'AND' | 'OR';
        filters: Array<{
          field: string;
          table?: 'transactions' | 'transaction_details';
          value: string | number;
          dataType: 'string' | 'number' | 'date' | 'boolean';
          operator: '=' | '!=' | '>' | '<' | '>=' | '<=' | 'LIKE' | 'IN';
        }>;
      } | Record<string, never>;
    };
  };
  meta_analysis: {
    metaAnalysisResults: MetaAnalysisResults;
  };
  status: 'enabled' | 'disabled' | 'expired';
  created_at: string;
  updated_at: string;
}

export type DataFrequencyCategory = 'SuperHigh' | 'VeryHigh' | 'High' | 'Med' | 'Low' | 'VeryLow';

export interface MetaAnalysisResults {
  dailyAvgEntriesLast7DaysExclNullDays: number;
  dailyAvgEntriesLast30DaysExclNullDays: number;
  numberOfNullDaysLast30Days: number;
  interArrivalTimeDistributionMeanLastXDays: number;
  interArrivalTimeDistributionNormalizedMeanLast40Transactions: number;
  interArrivalTimeDistributionStdDevNormalizedLast40Transactions: number;
  dataFrequencyCategory: DataFrequencyCategory;
  avgSourceDataLag: number;
  weeklyCyclicalityMeasureAnovaFStatistic: number;
  dailyCyclicalityMeasureAnovaFStatistic: number | null;
  historicalContextType: 'same_day_of_the_week' | 'previous_periods';
  objectiveQuantitativeFields: string[];
  itemCodeName?: string;
}

/**
 * Echo of the entry's `analysisFilter` shape — see `DynamicJobEntry`. Either
 * a real filter group or an empty `{}` when the entry is the unfiltered baseline.
 */
export type DynamicEntryFilter =
  | DynamicJobEntry['dynamic_config']['dynamicAnalysisConfig']['analysisFilter']
  | undefined;

export interface DynamicEntryResult {
  id: string;
  analysis_jobs_dynamic_id: string;
  run_id?: string;
  // The analysis result payload is a polymorphic union driven by the parent
  // job's analysis type (ghostTransactionAnalysis, aggregatePerformanceAnalysis,
  // …). Each variant has its own deeply-nested shape that the result-detail
  // pages walk explicitly. The backend has no shared schema we can import here,
  // and modelling every variant on the frontend would be both noisy and
  // out-of-date by the time it shipped. Keep this opaque at the wire boundary
  // and let the consumers narrow per-variant.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- undocumented polymorphic backend contract; see comment above.
  result_data: any;
  significance_status: boolean;
  execution_time_seconds?: number;
  metadata?: Record<string, unknown>;
  error_message?: string;
  created_at: string;
  entry_info?: {
    id: string;
    filter: DynamicEntryFilter;
    analysisLevel: string;
    metaAnalysis?: MetaAnalysisResults;
  };
}

export interface AnalysisExecutionResult {
  success: boolean;
  parentJobId: string;
  progress: {
    totalEntries: number;
    processedEntries: number;
    successCount: number;
    errorCount: number;
  };
  executionTimeMs: number;
  message: string;
}

export interface CreateExecutionJobDto {
  analysis_name?: string;
  analysis_description?: string;
  schedule?: string; // Cron expression, default: '*/10 * * * *'
  enabled?: boolean;
}

export interface UpdateExecutionJobDto {
  analysis_name?: string;
  analysis_description?: string;
  schedule?: string;
  enabled?: boolean;
}

export interface DataSource {
  id: string;
  name: string;
  type: string;
  status: string;
}

export interface DataSourceAccount {
  id: string;
  name: string;
  accountType: string;
  institutionName?: string;
}

// ============================================================================
// API SERVICE
// ============================================================================

export const dynamicAnalysisApi = {
  // Job Management
  async getOrganizationJobs(): Promise<{ jobs: DynamicAnalysisJob[] }> {
    const response = await api.get('/api/auto-dynamic-analysis/jobs');
    // Backend returns { success, data, count }
    return { jobs: response.data.data || [] };
  },

  async getJobDetails(jobId: string): Promise<{ 
    job: DynamicAnalysisJob; 
    outputChannel: OutputChannelConfig | null;
  }> {
    const response = await api.get(`/api/auto-dynamic-analysis/jobs/${jobId}`);
    const data = response.data.data;
    const jobData = data.job || data;
    const enabled = jobData.enabled ?? (jobData.status === 'active');
    return { 
      job: { ...jobData, enabled },
      outputChannel: data.outputChannel ?? jobData.config?.outputChannel ?? null,
    };
  },

  async deleteJob(jobId: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/api/auto-dynamic-analysis/jobs/${jobId}`);
    return { success: response.data.success };
  },

  async enableJob(jobId: string): Promise<{ job: DynamicAnalysisJob }> {
    await api.post(`/api/auto-dynamic-analysis/jobs/${jobId}/enable`);
    // After enabling, refetch the job to get updated data
    return this.getJobDetails(jobId);
  },

  async disableJob(jobId: string): Promise<{ job: DynamicAnalysisJob }> {
    await api.post(`/api/auto-dynamic-analysis/jobs/${jobId}/disable`);
    // After disabling, refetch the job to get updated data
    return this.getJobDetails(jobId);
  },

  async runJob(jobId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/api/auto-dynamic-analysis/jobs/${jobId}/run`);
    return { success: response.data.success, message: response.data.message };
  },

  // Dynamic Job Entries
  async getJobEntries(jobId: string): Promise<{ entries: DynamicJobEntry[] }> {
    const response = await api.get(`/api/auto-dynamic-analysis/jobs/${jobId}/entries`);
    // Backend returns { success, data, count }
    return { entries: response.data.data || [] };
  },

  async getEntryDetails(entryId: string): Promise<{ entry: DynamicJobEntry }> {
    const response = await api.get(`/api/auto-dynamic-analysis/entries/${entryId}`);
    return { entry: response.data.data };
  },

  async getEntryResults(entryId: string, limit: number = 10): Promise<{ results: DynamicEntryResult[] }> {
    const response = await api.get(`/api/auto-dynamic-analysis/entries/${entryId}/results`, {
      params: { limit }
    });
    return { results: response.data.data || [] };
  },

  // Get single result by ID
  async getResultById(resultId: string): Promise<{ result: DynamicEntryResult }> {
    const response = await api.get(`/api/auto-dynamic-analysis/results/${resultId}`);
    return { result: response.data.data };
  },

  // Analysis Execution
  async executeJobAnalysis(jobId: string): Promise<{ result: AnalysisExecutionResult }> {
    const response = await api.post(`/api/auto-dynamic-analysis/jobs/${jobId}/execute-analysis`);
    return { result: response.data.data };
  },

  async getJobResults(jobId: string, limit: number = 50, dynamicJobId?: string, runId?: string): Promise<{ 
    results: DynamicEntryResult[]; 
    totalEntries: number 
  }> {
    const params: { limit: number; dynamicJobId?: string; runId?: string } = { limit };
    if (dynamicJobId && dynamicJobId.trim()) {
      params.dynamicJobId = dynamicJobId.trim();
    }
    if (runId && runId.trim()) {
      params.runId = runId.trim();
    }
    const response = await api.get(`/api/auto-dynamic-analysis/jobs/${jobId}/all-results`, {
      params
    });
    return { 
      results: response.data.data || [], 
      totalEntries: response.data.totalEntries || 0 
    };
  },

  // Selection Endpoints
  async getDataSources(): Promise<{ dataSources: DataSource[] }> {
    const response = await api.get('/api/auto-dynamic-analysis/data-sources');
    return { dataSources: response.data.data || [] };
  },

  async getDataSourceAccounts(dataSourceId: string): Promise<{ accounts: DataSourceAccount[] }> {
    const response = await api.get(`/api/auto-dynamic-analysis/data-sources/${dataSourceId}/accounts`);
    return { accounts: response.data.data || [] };
  },

  // ============================================================================
  // EXECUTION JOB METHODS (Phase 2)
  // ============================================================================

  async createExecutionJob(parentJobId: string, data: CreateExecutionJobDto): Promise<{ job: ExecutionJob }> {
    const response = await api.post(`/api/auto-dynamic-analysis/jobs/${parentJobId}/execution-job`, data);
    return { job: response.data.data };
  },

  async getExecutionJobByParent(parentJobId: string): Promise<{ job: ExecutionJob | null; exists: boolean }> {
    const response = await api.get(`/api/auto-dynamic-analysis/jobs/${parentJobId}/execution-job`);
    return { job: response.data.data, exists: response.data.exists };
  },

  async getExecutionJobDetails(executionJobId: string): Promise<{ job: ExecutionJob }> {
    const response = await api.get(`/api/auto-dynamic-analysis/execution-jobs/${executionJobId}`);
    return { job: response.data.data };
  },

  async updateExecutionJob(executionJobId: string, data: UpdateExecutionJobDto): Promise<{ job: ExecutionJob }> {
    const response = await api.put(`/api/auto-dynamic-analysis/execution-jobs/${executionJobId}`, data);
    return { job: response.data.data };
  },

  async enableExecutionJob(executionJobId: string): Promise<{ job: ExecutionJob }> {
    const response = await api.post(`/api/auto-dynamic-analysis/execution-jobs/${executionJobId}/enable`);
    return { job: response.data.data };
  },

  async disableExecutionJob(executionJobId: string): Promise<{ job: ExecutionJob }> {
    const response = await api.post(`/api/auto-dynamic-analysis/execution-jobs/${executionJobId}/disable`);
    return { job: response.data.data };
  },

  async deleteExecutionJob(executionJobId: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/api/auto-dynamic-analysis/execution-jobs/${executionJobId}`);
    return { success: response.data.success };
  },

};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Schedule presets for definition jobs (Phase 1 - typically daily)
export const DEFINITION_SCHEDULE_PRESETS = [
  { value: '0 0 * * *', label: 'Daily at midnight' },
  { value: '0 6 * * *', label: 'Daily at 6 AM' },
  { value: '0 12 * * *', label: 'Daily at noon' },
  { value: '0 18 * * *', label: 'Daily at 6 PM' },
  { value: '0 0 * * 0', label: 'Weekly (Sunday midnight)' },
  { value: '0 0 * * 1', label: 'Weekly (Monday midnight)' },
  { value: '0 0 1 * *', label: 'Monthly (1st at midnight)' },
];

// Schedule presets for execution jobs (Phase 2 - more frequent)
export const EXECUTION_SCHEDULE_PRESETS = [
  { value: '*/5 * * * *', label: 'Every 5 minutes' },
  { value: '*/10 * * * *', label: 'Every 10 minutes' },
  { value: '*/15 * * * *', label: 'Every 15 minutes' },
  { value: '*/30 * * * *', label: 'Every 30 minutes' },
  { value: '0 * * * *', label: 'Every hour' },
  { value: '0 */2 * * *', label: 'Every 2 hours' },
  { value: '0 */4 * * *', label: 'Every 4 hours' },
  { value: '0 */6 * * *', label: 'Every 6 hours' },
  { value: '0 */12 * * *', label: 'Every 12 hours' },
];

// Legacy alias
export const SCHEDULE_PRESETS = DEFINITION_SCHEDULE_PRESETS;

export function getScheduleLabel(cron: string): string {
  const preset = SCHEDULE_PRESETS.find(p => p.value === cron);
  return preset?.label || cron;
}

export function getEntryStatusVariant(status: string): 'success' | 'neutral' | 'warning' | 'error' {
  switch (status) {
    case 'enabled': return 'success';
    case 'disabled': return 'neutral';
    case 'expired': return 'warning';
    default: return 'neutral';
  }
}

